import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import type { Invoice } from '@efacture/web-billing/api/queries/invoices';
import type { DecimalString } from '@efacture/web-billing/lib/decimal';
import type { QuotePrintVariant } from '@efacture/core/sales';

// ─────────────────────────────────────────────────────────────
// Module Ventes (P1) — devis, bons de livraison, factures
// récurrentes, relances. Endpoints montés sous /api/sales par
// l'agent de câblage final (voir packages/api/src/routes/sales).
//
// Rappels d'unwrap : apiClient retire l'enveloppe { success, data }.
//   - listes : { quotes, pagination } / { deliveryNotes, pagination }
//     / { recurringInvoices } sont renvoyées directement ;
//   - détail/mutations : { quote } / { deliveryNote } /
//     { recurringInvoice } / { policy } / { reminders } / { invoice }
//     → on extrait le champ nommé.
// Les Decimal Prisma arrivent en string JSON (number | string).
// ─────────────────────────────────────────────────────────────

export interface PaginationInfo {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasMore: boolean;
}

export type PriceMode = 'HT' | 'TTC';
/** Code of the client-type table (DGI codes plus the tenant's rows, §2.35). */
export type SalesClientType = string;

// ─────────────────────────────────────────────────────────────
// Devis / proformas
// ─────────────────────────────────────────────────────────────

export type QuoteStatus =
  | 'DRAFT'
  | 'SENT'
  | 'ACCEPTED'
  | 'REJECTED'
  | 'EXPIRED'
  | 'CONVERTED';

export const QUOTE_STATUS_LABELS: Record<QuoteStatus, string> = {
  DRAFT: 'Brouillon',
  SENT: 'Envoyé',
  ACCEPTED: 'Accepté',
  REJECTED: 'Refusé',
  EXPIRED: 'Expiré',
  CONVERTED: 'Converti',
};

export interface QuoteLine {
  id: string;
  quoteId: string;
  lineNumber: number;
  articleId: string | null;
  articleCode: string | null;
  articleName: string;
  articleType: string;
  taxGroup: string;
  /** PSVB group, TSB flag and specific-tax parameters copied from the article. */
  psvbGroup: string | null;
  isBeverage: boolean;
  specificTaxMode: string | null;
  specificTaxRate: DecimalString | null;
  specificTaxUnitAmount: DecimalString | null;
  specificTaxLabel: string | null;
  quantity: DecimalString;
  unitPrice: DecimalString;
  discount: DecimalString;
  totalHT: DecimalString;
  totalTVA: DecimalString;
  totalTSB: DecimalString;
  totalSpecificTax: DecimalString;
  totalPSVB: DecimalString;
  totalTTC: DecimalString;
}

export interface Quote {
  id: string;
  tenantId: string;
  number: string;
  status: QuoteStatus;
  clientId: string | null;
  clientType: SalesClientType;
  clientName: string | null;
  clientIfu: string | null;
  priceMode: PriceMode;
  validUntil: string | null;
  totalHT: DecimalString;
  totalTVA: DecimalString;
  /** TSB and specific tax, inside totalTTC; PSVB, outside it. */
  totalTSB: DecimalString;
  totalSpecificTax: DecimalString;
  totalPSVB: DecimalString;
  totalTTC: DecimalString;
  notes: string | null;
  /** Proforma fields (#538): the title it prints under by default, DEVIS or PROFORMA. */
  printVariant: QuotePrintVariant | null;
  deliveryDelay: string | null;
  /** Function, then name, one per line. */
  signatory: string | null;
  showAmountInWords: boolean | null;
  convertedInvoiceId: string | null;
  createdById: string | null;
  createdAt: string;
  updatedAt: string;
  lines: QuoteLine[];
}

export interface QuoteLineInput {
  articleId: string;
  articleCode?: string;
  articleName: string;
  articleType: string;
  taxGroup: string;
  quantity: number;
  unitPrice: number;
  /** Montant absolu en FCFA (comme le moteur de taxes), pas un pourcentage. */
  discount?: number;
}

export interface CreateQuoteInput {
  clientId?: string | undefined;
  clientType?: SalesClientType | undefined;
  clientName?: string | undefined;
  clientIfu?: string | undefined;
  priceMode?: PriceMode | undefined;
  validUntil?: string | undefined;
  notes?: string | undefined;
  printVariant?: QuotePrintVariant | null | undefined;
  deliveryDelay?: string | null | undefined;
  signatory?: string | null | undefined;
  showAmountInWords?: boolean | null | undefined;
  lines: QuoteLineInput[];
}

export type UpdateQuoteInput = Partial<CreateQuoteInput> & {
  /** The `updatedAt` of the quote the form loaded: a stale save is refused (409). */
  updatedAt?: string | undefined;
};

export interface QuoteListParams {
  status?: QuoteStatus | '' | undefined;
  clientId?: string | undefined;
  search?: string | undefined;
  page?: number | undefined;
  limit?: number | undefined;
}

export interface QuoteListResponse {
  quotes: Quote[];
  pagination: PaginationInfo;
}

// ─────────────────────────────────────────────────────────────
// Bons de livraison
// ─────────────────────────────────────────────────────────────

export type DeliveryNoteStatus = 'DRAFT' | 'DELIVERED' | 'CANCELLED';

export const DELIVERY_NOTE_STATUS_LABELS: Record<DeliveryNoteStatus, string> = {
  DRAFT: 'Brouillon',
  DELIVERED: 'Livré',
  CANCELLED: 'Annulé',
};

export interface DeliveryNoteLine {
  id: string;
  deliveryNoteId: string;
  lineNumber: number;
  articleId: string | null;
  articleCode: string | null;
  articleName: string;
  quantity: DecimalString;
  unit: string | null;
}

export interface DeliveryNote {
  id: string;
  tenantId: string;
  number: string;
  status: DeliveryNoteStatus;
  invoiceId: string | null;
  clientId: string | null;
  clientName: string | null;
  deliveredAt: string | null;
  receivedBy: string | null;
  notes: string | null;
  createdById: string | null;
  createdAt: string;
  updatedAt: string;
  lines: DeliveryNoteLine[];
}

export interface DeliveryNoteLineInput {
  articleId?: string;
  articleCode?: string;
  articleName: string;
  quantity: number;
  unit?: string;
}

export interface CreateDeliveryNoteInput {
  invoiceId?: string;
  clientId?: string;
  clientName?: string;
  notes?: string;
  lines?: DeliveryNoteLineInput[];
}

export interface DeliveryNoteListParams {
  status?: DeliveryNoteStatus | '' | undefined;
  invoiceId?: string | undefined;
  search?: string | undefined;
  page?: number | undefined;
  limit?: number | undefined;
}

export interface DeliveryNoteListResponse {
  deliveryNotes: DeliveryNote[];
  pagination: PaginationInfo;
}

// ─────────────────────────────────────────────────────────────
// Factures récurrentes
// ─────────────────────────────────────────────────────────────

export type RecurringFrequency =
  | 'MONTHLY'
  | 'QUARTERLY'
  | 'SEMIANNUAL'
  | 'YEARLY';

export const RECURRING_FREQUENCY_LABELS: Record<RecurringFrequency, string> = {
  MONTHLY: 'Mensuelle',
  QUARTERLY: 'Trimestrielle',
  SEMIANNUAL: 'Semestrielle',
  YEARLY: 'Annuelle',
};

export interface RecurringLineInput {
  articleId: string;
  articleCode?: string;
  articleName: string;
  articleType: string;
  taxGroup: string;
  quantity: number;
  unitPrice: number;
  /** Montant absolu en FCFA (comme le moteur de taxes). */
  discount?: number;
}

export interface RecurringTemplate {
  priceMode: PriceMode;
  notes?: string | null;
  lines: RecurringLineInput[];
  meta?: { createdById: string; consecutiveFailures: number };
}

export interface RecurringInvoice {
  id: string;
  tenantId: string;
  name: string;
  clientId: string;
  frequency: RecurringFrequency;
  dayOfMonth: number;
  nextRunAt: string;
  lastRunAt: string | null;
  isActive: boolean;
  template: RecurringTemplate;
  createdAt: string;
  updatedAt: string;
}

export interface CreateRecurringInput {
  name: string;
  clientId: string;
  frequency: RecurringFrequency;
  dayOfMonth?: number | undefined;
  priceMode?: PriceMode | undefined;
  notes?: string | undefined;
  startDate?: string | undefined;
  lines: RecurringLineInput[];
}

export interface UpdateRecurringInput {
  name?: string | undefined;
  frequency?: RecurringFrequency | undefined;
  dayOfMonth?: number | undefined;
  priceMode?: PriceMode | undefined;
  notes?: string | undefined;
  isActive?: boolean | undefined;
  lines?: RecurringLineInput[] | undefined;
  /** The `updatedAt` of the template the form loaded: a stale save is refused (409). */
  updatedAt?: string | undefined;
}

// ─────────────────────────────────────────────────────────────
// Relances de recouvrement
// ─────────────────────────────────────────────────────────────

export type ReminderChannel = 'EMAIL' | 'SMS' | 'WHATSAPP';

export const REMINDER_CHANNEL_LABELS: Record<ReminderChannel, string> = {
  EMAIL: 'E-mail',
  SMS: 'SMS',
  WHATSAPP: 'WhatsApp',
};

export interface ReminderLevel {
  afterDays: number;
  channel: ReminderChannel;
  template?: string;
}

export interface ReminderPolicy {
  isActive: boolean;
  levels: ReminderLevel[];
}

export interface ReminderPolicyInput {
  isActive: boolean;
  levels: ReminderLevel[];
}

export interface Reminder {
  id: string;
  level: number;
  channel: string;
  status: string;
  sentAt: string;
  notes: string | null;
}

// ─────────────────────────────────────────────────────────────
// Query keys
// ─────────────────────────────────────────────────────────────

export const salesKeys = {
  all: ['sales'] as const,
  quotes: () => [...salesKeys.all, 'quotes'] as const,
  quoteList: (params: QuoteListParams) => [...salesKeys.quotes(), 'list', params] as const,
  quoteDetail: (id: string) => [...salesKeys.quotes(), 'detail', id] as const,
  deliveryNotes: () => [...salesKeys.all, 'delivery-notes'] as const,
  deliveryNoteList: (params: DeliveryNoteListParams) =>
    [...salesKeys.deliveryNotes(), 'list', params] as const,
  deliveryNoteDetail: (id: string) => [...salesKeys.deliveryNotes(), 'detail', id] as const,
  recurring: () => [...salesKeys.all, 'recurring'] as const,
  recurringList: (isActive?: boolean) => [...salesKeys.recurring(), 'list', isActive] as const,
  recurringDetail: (id: string) => [...salesKeys.recurring(), 'detail', id] as const,
  reminderPolicy: () => [...salesKeys.all, 'reminder-policy'] as const,
  reminders: (invoiceId: string) => [...salesKeys.all, 'reminders', invoiceId] as const,
};

// ─────────────────────────────────────────────────────────────
// Devis — API + hooks
// ─────────────────────────────────────────────────────────────

async function fetchQuotes(params: QuoteListParams): Promise<QuoteListResponse> {
  return apiClient.get<QuoteListResponse>('/sales/quotes', {
    status: params.status || undefined,
    clientId: params.clientId,
    search: params.search,
    page: params.page,
    limit: params.limit,
  });
}

async function fetchQuote(id: string): Promise<Quote> {
  const data = await apiClient.get<{ quote: Quote }>(`/sales/quotes/${id}`);
  return data.quote;
}

export function useQuotes(params: QuoteListParams = {}, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: salesKeys.quoteList(params),
    queryFn: () => fetchQuotes(params),
    staleTime: 1000 * 60 * 2,
    enabled: options?.enabled ?? true,
  });
}

export function useQuote(id: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: salesKeys.quoteDetail(id),
    queryFn: () => fetchQuote(id),
    staleTime: 1000 * 60 * 2,
    enabled: (options?.enabled ?? true) && !!id,
  });
}

export function useCreateQuote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateQuoteInput) => {
      const data = await apiClient.post<{ quote: Quote }>('/sales/quotes', input);
      return data.quote;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: salesKeys.quotes() });
    },
  });
}

export function useUpdateQuote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, input }: { id: string; input: UpdateQuoteInput }) => {
      const data = await apiClient.put<{ quote: Quote }>(`/sales/quotes/${id}`, input);
      return data.quote;
    },
    onSuccess: (quote) => {
      qc.setQueryData(salesKeys.quoteDetail(quote.id), quote);
      qc.invalidateQueries({ queryKey: salesKeys.quotes() });
    },
  });
}

export function useDeleteQuote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiClient.delete<{ deleted: boolean }>(`/sales/quotes/${id}`),
    onSuccess: (_, id) => {
      qc.removeQueries({ queryKey: salesKeys.quoteDetail(id) });
      qc.invalidateQueries({ queryKey: salesKeys.quotes() });
    },
  });
}

/** Transition simple (send/accept/reject) : renvoie le devis mis à jour. */
function quoteTransition(action: 'send' | 'accept' | 'reject') {
  return async (id: string): Promise<Quote> => {
    const data = await apiClient.post<{ quote: Quote }>(`/sales/quotes/${id}/${action}`);
    return data.quote;
  };
}

export function useSendQuote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: quoteTransition('send'),
    onSuccess: (quote) => {
      qc.setQueryData(salesKeys.quoteDetail(quote.id), quote);
      qc.invalidateQueries({ queryKey: salesKeys.quotes() });
    },
  });
}

export function useAcceptQuote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: quoteTransition('accept'),
    onSuccess: (quote) => {
      qc.setQueryData(salesKeys.quoteDetail(quote.id), quote);
      qc.invalidateQueries({ queryKey: salesKeys.quotes() });
    },
  });
}

export function useRejectQuote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: quoteTransition('reject'),
    onSuccess: (quote) => {
      qc.setQueryData(salesKeys.quoteDetail(quote.id), quote);
      qc.invalidateQueries({ queryKey: salesKeys.quotes() });
    },
  });
}

/** Conversion → nouvelle facture DRAFT. Renvoie la facture créée. */
/** The levy parameters of a line: tax group, TSB flag, PSVB group, specific tax. */
export interface LineLevies {
  taxGroup: string;
  isBeverage: boolean;
  psvbGroup: string | null;
  specificTax:
    | { mode: 'AD_VALOREM'; rate: number; label: string }
    | { mode: 'FIXED_PER_UNIT'; unitAmount: number; label: string }
    | null;
}

/** A quote line whose levies differ on the invoice it converted to. */
export interface QuoteLevyChange {
  lineNumber: number;
  articleName: string;
  before: LineLevies;
  after: LineLevies;
}

export interface QuoteConversion {
  invoice: Invoice;
  levyChanges: QuoteLevyChange[];
}

export function useConvertQuote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string): Promise<QuoteConversion> =>
      apiClient.post<QuoteConversion>(`/sales/quotes/${id}/convert`),
    onSuccess: (_invoice, id) => {
      qc.invalidateQueries({ queryKey: salesKeys.quoteDetail(id) });
      qc.invalidateQueries({ queryKey: salesKeys.quotes() });
    },
  });
}

/** Downloads the quote printed as a « Devis » or as a « Facture proforma » (#538). */
export function useDownloadQuotePdf() {
  return useMutation({
    mutationFn: ({ id, number, variant }: { id: string; number: string; variant: QuotePrintVariant }) =>
      apiClient.download(
        `/sales/quotes/${id}/pdf?variant=${variant}`,
        `${variant === 'PROFORMA' ? 'proforma' : 'devis'}-${number}.pdf`
      ),
  });
}

// ─────────────────────────────────────────────────────────────
// Bons de livraison — API + hooks
// ─────────────────────────────────────────────────────────────

async function fetchDeliveryNotes(
  params: DeliveryNoteListParams
): Promise<DeliveryNoteListResponse> {
  return apiClient.get<DeliveryNoteListResponse>('/sales/delivery-notes', {
    status: params.status || undefined,
    invoiceId: params.invoiceId,
    search: params.search,
    page: params.page,
    limit: params.limit,
  });
}

export function useDeliveryNotes(
  params: DeliveryNoteListParams = {},
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: salesKeys.deliveryNoteList(params),
    queryFn: () => fetchDeliveryNotes(params),
    staleTime: 1000 * 60 * 2,
    enabled: options?.enabled ?? true,
  });
}

export function useCreateDeliveryNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateDeliveryNoteInput) => {
      const data = await apiClient.post<{ deliveryNote: DeliveryNote }>(
        '/sales/delivery-notes',
        input
      );
      return data.deliveryNote;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: salesKeys.deliveryNotes() });
    },
  });
}

export function useDeliverDeliveryNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, receivedBy }: { id: string; receivedBy?: string | undefined }) => {
      const data = await apiClient.post<{ deliveryNote: DeliveryNote }>(
        `/sales/delivery-notes/${id}/deliver`,
        { receivedBy }
      );
      return data.deliveryNote;
    },
    onSuccess: (note) => {
      qc.setQueryData(salesKeys.deliveryNoteDetail(note.id), note);
      qc.invalidateQueries({ queryKey: salesKeys.deliveryNotes() });
    },
  });
}

export function useCancelDeliveryNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const data = await apiClient.post<{ deliveryNote: DeliveryNote }>(
        `/sales/delivery-notes/${id}/cancel`
      );
      return data.deliveryNote;
    },
    onSuccess: (note) => {
      qc.setQueryData(salesKeys.deliveryNoteDetail(note.id), note);
      qc.invalidateQueries({ queryKey: salesKeys.deliveryNotes() });
    },
  });
}

export function useDownloadDeliveryNotePdf() {
  return useMutation({
    mutationFn: ({
      id,
      number,
      withPrices,
    }: {
      id: string;
      number: string;
      withPrices?: boolean;
    }) =>
      apiClient.download(
        `/sales/delivery-notes/${id}/pdf${withPrices ? '?withPrices=true' : ''}`,
        `bl-${number}.pdf`
      ),
  });
}

// ─────────────────────────────────────────────────────────────
// Factures récurrentes — API + hooks
// ─────────────────────────────────────────────────────────────

export function useRecurringInvoices(
  params: { isActive?: boolean } = {},
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: salesKeys.recurringList(params.isActive),
    queryFn: () =>
      apiClient.get<{ recurringInvoices: RecurringInvoice[] }>('/sales/recurring-invoices', {
        isActive: params.isActive,
      }),
    staleTime: 1000 * 60 * 2,
    enabled: options?.enabled ?? true,
  });
}

export function useRecurringInvoice(id: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: salesKeys.recurringDetail(id),
    queryFn: async () => {
      const data = await apiClient.get<{ recurringInvoice: RecurringInvoice }>(
        `/sales/recurring-invoices/${id}`
      );
      return data.recurringInvoice;
    },
    staleTime: 1000 * 60 * 2,
    enabled: (options?.enabled ?? true) && !!id,
  });
}

export function useCreateRecurringInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateRecurringInput) => {
      const data = await apiClient.post<{ recurringInvoice: RecurringInvoice }>(
        '/sales/recurring-invoices',
        input
      );
      return data.recurringInvoice;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: salesKeys.recurring() });
    },
  });
}

export function useUpdateRecurringInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, input }: { id: string; input: UpdateRecurringInput }) => {
      const data = await apiClient.put<{ recurringInvoice: RecurringInvoice }>(
        `/sales/recurring-invoices/${id}`,
        input
      );
      return data.recurringInvoice;
    },
    onSuccess: (recurringInvoice) => {
      qc.setQueryData(salesKeys.recurringDetail(recurringInvoice.id), recurringInvoice);
      qc.invalidateQueries({ queryKey: salesKeys.recurring() });
    },
  });
}

export function useDeleteRecurringInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiClient.delete<{ deleted: boolean }>(`/sales/recurring-invoices/${id}`),
    onSuccess: (_, id) => {
      qc.removeQueries({ queryKey: salesKeys.recurringDetail(id) });
      qc.invalidateQueries({ queryKey: salesKeys.recurring() });
    },
  });
}

// ─────────────────────────────────────────────────────────────
// Relances — API + hooks
// ─────────────────────────────────────────────────────────────

export function useReminderPolicy(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: salesKeys.reminderPolicy(),
    queryFn: async () => {
      return apiClient.get<{ policy: ReminderPolicy | null; availableChannels: ReminderChannel[] }>(
        '/sales/reminder-policy'
      );
    },
    staleTime: 1000 * 60 * 2,
    enabled: options?.enabled ?? true,
  });
}

export function useUpsertReminderPolicy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: ReminderPolicyInput) => {
      const data = await apiClient.put<{ policy: ReminderPolicy }>(
        '/sales/reminder-policy',
        input
      );
      return data.policy;
    },
    onSuccess: (policy) => {
      qc.setQueryData(salesKeys.reminderPolicy(), policy);
    },
  });
}

export function useReminders(invoiceId: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: salesKeys.reminders(invoiceId),
    queryFn: async () => {
      const data = await apiClient.get<{ reminders: Reminder[] }>('/sales/reminders', {
        invoiceId,
      });
      return data.reminders;
    },
    staleTime: 1000 * 60,
    enabled: (options?.enabled ?? true) && !!invoiceId,
  });
}
